import { storeSession } from '@/generated/stores';
import { type AxiosResponse } from 'axios';
import { getRequestConfig } from '@/framework/base/helpers/HttpHelper.ts';
import Contrat, { ContratDpaeStatuts } from '@/generated/classes/Contrat';

// Logique de la génération des DPAE en masse, isolée de l'UI.
//
// Le lot est traité côté serveur : `POST api/contrats/dpaeMasse` porte la liste des ids et rend un
// compte rendu ventilé. Le client découpe en tranches pour garder une barre d'avancement et borner la
// durée de chaque requête, mais ne décide plus de rien.
//
// Ce que le passage au lot a déplacé côté serveur :
//  - l'éligibilité, relue en base à chaque contrat. Le client ne transmet plus ni `statut` ni
//    `actionIndex` — cet index, qui désignait une action dans le tableau de l'ÉTAT COURANT et valait
//    « Ne pas envoyer » sur un contrat en ATTENTE_ENVOI, ne circule plus sur le réseau.
//  - le motif d'échec, désormais rendu par contrat au lieu d'être écrasé en « erreur technique ».
//
// À noter : « Générer » n'envoie rien à l'URSSAF. Il fait passer le contrat en ATTENTE_ENVOI ; l'envoi
// réel est effectué plus tard par un traitement serveur planifié, qui regroupe déjà tous les contrats
// en attente en un seul appel au middleware CI98.

/**
 * Statuts sur lesquels le serveur acceptera d'agir.
 *
 * ⚠️ Ce n'est plus un garde-fou : la décision appartient au serveur, qui relit le statut en base.
 * Cette liste ne sert qu'à ventiler l'affichage de la confirmation, et une divergence avec le serveur
 * fausserait au pire un décompte annoncé, jamais un traitement.
 */
const STATUTS_ELIGIBLES: readonly ContratDpaeStatuts[] = [
  ContratDpaeStatuts.PROPOSEE,
  ContratDpaeStatuts.OBLIGATOIRE,
];

/** Statuts pour lesquels une DPAE a déjà été armée, envoyée ou traitée par l'URSSAF. */
const STATUTS_DEJA_REALISES: readonly ContratDpaeStatuts[] = [
  ContratDpaeStatuts.ATTENTE_ENVOI,
  ContratDpaeStatuts.ENVOYEE,
  ContratDpaeStatuts.RETOUR_OK,
  ContratDpaeStatuts.RETOUR_INVALIDE,
];

/**
 * Taille des tranches envoyées au serveur.
 *
 * Une requête unique capterait tout le gain serveur mais supprimerait la progression et exposerait à
 * un délai d'attente : aucun `timeout` axios n'est posé côté client, c'est donc le navigateur ou IIS
 * qui trancherait, sans garantie et sans compte rendu. À 25, un lot de 200 contrats fait 8 requêtes
 * là où la boucle en faisait 200, et chaque requête reste courte.
 */
export const TAILLE_TRANCHE_DPAE = 25;

/** Ventilation d'une sélection de contrats au regard de la DPAE. */
export interface VentilationDpae {
  /** Contrats sur lesquels la génération peut être tentée. */
  eligibles: Contrat[];
  /** Contrats dont il manque des informations (salarié, état civil, données société). */
  informationsManquantes: number;
  /** Contrats dont la DPAE est déjà armée, envoyée ou traitée. */
  dejaRealisees: number;
  /** Contrats dont l'employeur s'est engagé à réaliser la DPAE lui-même. */
  nonGerees: number;
}

/**
 * Classe une sélection de contrats selon leur `dpaeStatut`. Aucun appel serveur : le statut porté par
 * le contrat suffit, la logique métier d'éligibilité ayant déjà été appliquée côté serveur au moment
 * où ce statut a été calculé.
 *
 * NON_GERE est compté à part et n'est jamais annoncé comme éligible : l'employeur a explicitement
 * choisi de déclarer lui-même, et le réintégrer exposerait à une double déclaration à l'URSSAF.
 */
export function ventilerDpae(contrats: Contrat[]): VentilationDpae {
  const ventilation: VentilationDpae = {
    eligibles: [],
    informationsManquantes: 0,
    dejaRealisees: 0,
    nonGerees: 0,
  };

  contrats.forEach((contrat) => {
    if (STATUTS_ELIGIBLES.includes(contrat.dpaeStatut)) {
      ventilation.eligibles.push(contrat);
    } else if (STATUTS_DEJA_REALISES.includes(contrat.dpaeStatut)) {
      ventilation.dejaRealisees++;
    } else if (contrat.dpaeStatut == ContratDpaeStatuts.NON_GERE) {
      ventilation.nonGerees++;
    } else {
      // SALARIE_INCONNU, INCOMPLETE_EMPLOYEUR, INCOMPLETE_SALARIE, et les contrats pas encore
      // arrivés à l'étape DPAE (NONE, INITIAL) : rien à générer pour le moment.
      ventilation.informationsManquantes++;
    }
  });

  return ventilation;
}

/** Contrat dont la génération a échoué, tel que rendu par le serveur. */
export interface DpaeMasseEchec {
  contratId: number;
  /** `dpaeStatut` constaté en base au moment du traitement. */
  statut: number;
  /** Motif court, affichable tel quel. */
  motif: string;
}

/**
 * Compte rendu d'un lot.
 *
 * Invariant tenu par le serveur, tranche par tranche : `succes + ignores + echecs.length == demandes`.
 */
export interface ResultatDpaeMasse {
  /** Contrats soumis au serveur. */
  demandes: number;
  /** Contrats passés en ATTENTE_ENVOI. */
  succes: number;
  /** Contrats non éligibles au moment du traitement : déjà générés, non gérés, ou incomplets. */
  ignores: number;
  /** Échecs, avec leur motif. */
  echecs: DpaeMasseEchec[];
}

/** Réponse brute du serveur pour une tranche. */
interface ReponseDpaeMasse {
  demandes: number;
  succes: number;
  ignores: number;
  echecs: DpaeMasseEchec[];
}

/**
 * Soumet une tranche d'ids au serveur.
 *
 * Contrairement aux classes générées, le rejet conserve la cause : c'est tout l'intérêt du lot côté
 * compte rendu, et l'écraser par un `reject(null)` reviendrait à la forme qu'on remplace.
 */
function traiterTranche(contratIds: number[]): Promise<ReponseDpaeMasse> {
  return new Promise((resolve, reject) => {
    if (storeSession.httpCx == undefined) {
      reject(new Error('Session non initialisée'));
      return;
    }
    storeSession.httpCx.post('api/contrats/dpaeMasse', { contratIds }, getRequestConfig())
      .then((res: AxiosResponse) => {
        if (res.status == 200) {
          resolve(res.data as ReponseDpaeMasse);
        } else {
          reject(res.data);
        }
      })
      .catch((err: unknown) => {
        reject(err);
      });
  });
}

/**
 * Génère les DPAE des contrats fournis, par tranches.
 *
 * Les tranches sont séquentielles à dessein : chaque contrat déclenche côté serveur une mise à jour
 * intégrale. Les paralléliser dégraderait le serveur pour tous les utilisateurs.
 *
 * Aucun échec n'interrompt le lot (règle de l'US). Une tranche entière qui échoue — coupure réseau,
 * 500 — est comptée en échecs sur chacun de ses contrats, et le lot continue avec la suivante : les
 * contrats des tranches déjà traitées sont acquis, rien ne justifie de les perdre de vue.
 *
 * `surProgression` est notifié après chaque tranche, pour alimenter le compteur d'avancement.
 */
export async function genererDpaeParLot(
  contrats: Contrat[],
  surProgression: (traites: number) => void,
  tailleTranche: number = TAILLE_TRANCHE_DPAE,
): Promise<ResultatDpaeMasse> {
  const resultat: ResultatDpaeMasse = { demandes: 0, succes: 0, ignores: 0, echecs: [] };
  const ids = contrats.map((contrat) => contrat.id);

  for (let debut = 0; debut < ids.length; debut += tailleTranche) {
    const tranche = ids.slice(debut, debut + tailleTranche);
    try {
      const reponse = await traiterTranche(tranche);
      resultat.demandes += reponse.demandes;
      resultat.succes += reponse.succes;
      resultat.ignores += reponse.ignores;
      resultat.echecs.push(...(reponse.echecs ?? []));
    } catch {
      resultat.demandes += tranche.length;
      tranche.forEach((contratId) => {
        resultat.echecs.push({ contratId, statut: 0, motif: 'Erreur technique' });
      });
    }
    surProgression(Math.min(debut + tailleTranche, ids.length));
  }

  return resultat;
}
